import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Headset } from 'lucide-react';
import { cn } from '@noor/lib/cn';
import { useMerchantChatEvents } from '../model/use-merchant-chat-events';
import { MerchantChatDrawer, type OpenMerchantChat } from './merchant-chat-drawer';

/**
 * The header chat entry — the old `TopBar` support button, ported. A round
 * utility button (the `AppSwitcher` twin) opening the chat drawer.
 *
 * Owns the open-thread id (not the drawer): the socket stays subscribed while
 * the shell is mounted, so pushes toast on every authed route and only skip
 * the toast when the drawer is open on that exact thread.
 */
export function MerchantChatCenter({ className }: { className?: string }) {
  const { t } = useTranslation('common');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatId, setChatId] = useState<OpenMerchantChat>(null);

  useMerchantChatEvents({
    openChatId: typeof chatId === 'number' ? chatId : null,
    drawerOpen,
  });

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        aria-label={t('chat.support')}
        className={cn(
          'relative flex size-14 shrink-0 cursor-pointer items-center justify-center rounded-full bg-muted text-foreground',
          'hover:bg-secondary-hover transition-colors outline-none',
          'focus-visible:ring-[3px] focus-visible:ring-ring/40',
          className,
        )}
      >
        <Headset className="size-6" />
      </button>
      <MerchantChatDrawer
        open={drawerOpen}
        chatId={chatId}
        onChatIdChange={setChatId}
        onClose={() => {
          setDrawerOpen(false);
          // The old drawer cleared the open thread on close, so the next
          // opening always lands on the list.
          setChatId(null);
        }}
      />
    </>
  );
}
